import type { MouseEvent, Ref } from "react";
import { animated, useSpring } from "@react-spring/web";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { hasOpenMenu } from "@design-system/Menu/hasOpenMenu";
import { useHasOpenModal } from "@design-system/overlay/openModalLock";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    ArrowDownIcon,
    ArrowUpIcon,
    CloseIcon,
    LeftNavClosedIcon,
    LeftNavOpenedIcon,
} from "@icons";
import styles from "./TaskBodyHeader.module.scss";

export interface TaskPagerProps {
    currentIndex: number;
    totalTasks: number;
    canGoToPreviousTask: boolean;
    canGoToNextTask: boolean;
    goToPreviousTask: () => void;
    goToNextTask: () => void;
    /** Names the paged item in the hotkey descriptions. */
    itemLabel?: string;
}

/**
 * "N / M" and the up and down buttons through the list's tasks, also on
 * Shift+K and Shift+J while each button is enabled (plain J and K move the
 * list's row focus).
 */
export const TaskPager = ({
    currentIndex,
    totalTasks,
    canGoToPreviousTask,
    canGoToNextTask,
    goToPreviousTask,
    goToNextTask,
    itemLabel = "task",
}: TaskPagerProps) => (
    <div className={styles.pager}>
        <span className={styles.pagerIndex}>
            {currentIndex} / {totalTasks}
        </span>
        <div className={styles.pagerButtons}>
            <WithHotkey
                hotKey="shift+k"
                hotKeyOptions={{
                    description: `Go to the previous ${itemLabel}`,
                }}
                tooltip="Navigate up"
            >
                <Button
                    variant="primary"
                    size="md"
                    iconOnly
                    onClick={goToPreviousTask}
                    disabled={!canGoToPreviousTask}
                    aria-label="Navigate up"
                >
                    <ArrowUpIcon />
                </Button>
            </WithHotkey>
            <WithHotkey
                hotKey="shift+j"
                hotKeyOptions={{ description: `Go to the next ${itemLabel}` }}
                tooltip="Navigate down"
            >
                <Button
                    variant="primary"
                    size="md"
                    iconOnly
                    onClick={goToNextTask}
                    disabled={!canGoToNextTask}
                    aria-label="Navigate down"
                >
                    <ArrowDownIcon />
                </Button>
            </WithHotkey>
        </div>
    </div>
);

/** Closes the open task; its tooltip names Escape, which `TasksPageLayout` binds. */
export const TaskCloseButton = ({ onClick }: { onClick: () => void }) => (
    <Tooltip content="Close" shortcut="escape">
        <Button
            variant="primary"
            size="md"
            iconOnly
            onClick={onClick}
            aria-label="Close"
        >
            <CloseIcon />
        </Button>
    </Tooltip>
);

const SPRING_CONFIG = { tension: 400, friction: 30 };
const iconClassName = "icon-color-gray-900 icon-rotate-180";
const PANE_TOGGLE_HOTKEY = "]";

interface TaskPaneToggleButtonProps {
    isPaneCollapsed: boolean;
    hasUnreadMessages: boolean;
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
    ref?: Ref<HTMLButtonElement>;
}

/**
 * Shows or hides the right pane, also on `]` while no menu or modal is open;
 * its icon crossfades between the two states.
 */
export const TaskPaneToggleButton = ({
    isPaneCollapsed,
    hasUnreadMessages,
    onClick,
    ref,
}: TaskPaneToggleButtonProps) => {
    const hasOpenModal = useHasOpenModal();
    const openSpring = useSpring({
        opacity: isPaneCollapsed ? 1 : 0,
        config: SPRING_CONFIG,
    });
    const closedSpring = useSpring({
        opacity: isPaneCollapsed ? 0 : 1,
        config: SPRING_CONFIG,
    });

    const tooltip = isPaneCollapsed ? "Show side panel" : "Hide side panel";
    const label =
        isPaneCollapsed && hasUnreadMessages
            ? `${tooltip}, unread messages`
            : tooltip;

    return (
        <WithHotkey
            hotKey={PANE_TOGGLE_HOTKEY}
            hotKeyOptions={{
                description: "Toggle side panel",
                useKey: true,
                ignoreEventWhen: () => hasOpenModal || hasOpenMenu(),
            }}
            tooltip={tooltip}
        >
            <Button
                variant="primary"
                size="md"
                iconOnly
                onClick={onClick}
                ref={ref}
                aria-label={label}
                aria-expanded={!isPaneCollapsed}
            >
                <span className={styles.iconSwap}>
                    <animated.span
                        className={styles.iconLayer}
                        style={openSpring}
                    >
                        <LeftNavOpenedIcon className={iconClassName} />
                    </animated.span>
                    <animated.span
                        className={styles.iconLayer}
                        style={closedSpring}
                    >
                        <LeftNavClosedIcon className={iconClassName} />
                    </animated.span>
                    {hasUnreadMessages && (
                        <span className={styles.unreadDot} aria-hidden="true" />
                    )}
                </span>
            </Button>
        </WithHotkey>
    );
};
